import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { QRCodeCanvas } from "qrcode.react";
import { useI18n } from "@/i18n";
import { getVenue, getMenus } from "@/api/endpoints";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QrCode, Download, FileDown, Copy, Check } from "lucide-react";

// Export QR'ı yüksek çözünürlükte üretmek için sabit boyut (px).
const EXPORT_SIZE = 1024;

interface MenuQrDialogProps {
  open: boolean;
  onClose: () => void;
  venueId: string;
  menuId: string;
}

// Tokensiz, doğrudan public menüye giden URL üretir. Backend'in /q/:shortCode
// redirect'ini (session token ekler) kullanmaz; QR client-side render edilir.
function buildMenuUrl(
  venue: { slug: string; customDomain?: string; customDomainStatus?: string } | undefined,
  menuSlug: string | undefined,
): string {
  if (!venue) return "";
  const domainActive = venue.customDomain && venue.customDomainStatus === "active";
  if (domainActive) {
    // Custom domain: venue domain'den çözülür, path'te yalnızca menü slug'ı olur.
    return menuSlug ? `https://${venue.customDomain}/${menuSlug}` : `https://${venue.customDomain}`;
  }
  const base = `https://menu.heremenu.app/${venue.slug}`;
  return menuSlug ? `${base}/${menuSlug}` : base;
}

export function MenuQrDialog({ open, onClose, venueId, menuId }: MenuQrDialogProps) {
  const { t } = useI18n();
  const exportRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const { data: venue } = useQuery({
    queryKey: ["venue", venueId],
    queryFn: () => getVenue(venueId),
    enabled: open && !!venueId,
  });

  const { data: menus } = useQuery({
    queryKey: ["menus", venueId],
    queryFn: () => getMenus(venueId),
    enabled: open && !!venueId,
  });

  const menu = menus?.find((m) => m.id === menuId);
  const url = buildMenuUrl(
    venue as { slug: string; customDomain?: string; customDomainStatus?: string } | undefined,
    menu?.slug,
  );

  const fileBase = `qr-${menu?.slug || venue?.slug || "menu"}`;

  // Export her zaman gizli 1024x1024 canvas'tan alınır (baskı kalitesi için).
  const getExportCanvas = () => exportRef.current?.querySelector("canvas") as HTMLCanvasElement | undefined;

  const handleDownloadPng = () => {
    const canvas = getExportCanvas();
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${fileBase}.png`;
    a.click();
  };

  const handleDownloadPdf = async () => {
    const canvas = getExportCanvas();
    if (!canvas) return;
    setPdfLoading(true);
    try {
      const { jsPDF } = await import("jspdf");
      // 1024x1024 px tek sayfalık PDF; QR sayfayı tam doldurur.
      const pdf = new jsPDF({ unit: "px", format: [EXPORT_SIZE, EXPORT_SIZE] });
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, EXPORT_SIZE, EXPORT_SIZE);
      pdf.save(`${fileBase}.pdf`);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="size-5" />
            {t("menu_qr_title")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <p className="text-center text-sm text-muted-foreground">{t("menu_qr_desc")}</p>

          <div className="rounded-xl border bg-white p-4">
            {url ? (
              <QRCodeCanvas value={url} size={220} level="M" marginSize={1} fgColor="#0f172a" />
            ) : (
              <div className="flex size-[220px] items-center justify-center">
                <QrCode className="size-10 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Export için gizli yüksek çözünürlüklü (1024px) canvas */}
          <div ref={exportRef} className="hidden" aria-hidden>
            {url && (
              <QRCodeCanvas value={url} size={EXPORT_SIZE} level="M" marginSize={2} fgColor="#0f172a" />
            )}
          </div>

          <div className="w-full">
            <p className="mb-1 text-xs text-muted-foreground">{t("menu_qr_link")}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md bg-muted px-2 py-1.5 text-xs">{url || "…"}</code>
              <Button variant="outline" size="icon-sm" onClick={handleCopy} disabled={!url}>
                {copied ? <Check className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          <div className="grid w-full grid-cols-2 gap-2">
            <Button variant="outline" onClick={handleDownloadPng} disabled={!url}>
              <Download className="mr-2 size-4" />
              {t("download_png")}
            </Button>
            <Button onClick={handleDownloadPdf} disabled={!url || pdfLoading}>
              <FileDown className="mr-2 size-4" />
              {pdfLoading ? t("loading") : t("download_pdf")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
